import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import Close from '@material-ui/icons/Close';
import { isEmpty, isEqual } from 'lodash';

import Hoverable from '~/components/Hoverable';
import TrashcanIcon from '~/components/icons/TrashcanIcon';
import attributeTypes from '~/components/SearchBuilder/utils/attributeTypes';
import SwitchGroup, { SwitchGroupItem } from '~/components/SwitchGroup';
import Switch from '~/components/SwitchWithLabel';
import TextFieldWithTooltip from '~/components/TextFieldWithTooltip';
import Tooltip from '~/components/Tooltip';
import ToastsContainer from '~/containers/Toasts';

import styles from './EditField.style';
import SelectOptions from './SelectOptions';
import {
  AUTOFILL_TYPES,
  HIDDEN_TYPES,
  LABEL_FOR_FIELD_TYPE,
  NO_PLACEHOLDER_TYPES,
} from './Utils';

class EditField extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      field: { ...props.field },
      errors: {},
    };
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    if (newProps.field) {
      this.setState({
        field: { ...newProps.field },
      });
    }
  }

  canAutofill = (field) => AUTOFILL_TYPES.includes(field.type);

  isHidden = (field) => HIDDEN_TYPES.includes(field.type);

  isVisible = (field) => !this.isHidden(field);

  hasPlaceholder = (field) => !NO_PLACEHOLDER_TYPES.includes(field.type);

  onDescriptionChange = (event) => {
    this.setState(
      {
        field: {
          ...this.state.field,
          description: event.target.value,
        },
      },
      this.update,
    );
  };

  onTypeChange = (event) => {
    let { value: type } = event.target;
    let { progressive, autofill, type: prevType } = this.state.field;

    if (prevType === 'email' && this.formHasAutofillFields()) {
      ToastsContainer.addError(
        'Disable autofill on other fields before removing email fields.',
      );
    }

    if (!this.canAutofill({ field: { type } })) {
      progressive = false;
      autofill = false;
    }

    this.setState(
      {
        field: {
          ...this.state.field,
          type,
          autofill,
          progressive,
        },
      },
      this.update,
    );
  };

  onLabelChange = (event) => {
    const { value: label } = event.target;
    let { field } = this.state;

    if (this.isVisible(field) && !label && !field.placeholder) {
      ToastsContainer.addWarning(
        'One of Placeholder or Label is required for this field.',
      );
      return this.setState({
        errors: {
          label: true,
          placeholder: true,
        },
      });
    }

    this.setState(
      {
        field: {
          ...field,
          label,
        },
      },
      this.update,
    );
  };

  onSlugChange = (event) => {
    let { value: slug } = event.target;

    if (!slug) {
      ToastsContainer.addWarning('Internal name is required');
      return this.setState({
        errors: {
          slug: true,
        },
      });
    }

    this.setState(
      {
        field: {
          ...this.state.field,
          slug,
        },
      },
      this.update,
    );
  };

  onPlaceholderChange = (event) => {
    let { value: placeholder } = event.target;
    let { field } = this.state;

    if (this.isVisible(field) && !placeholder && !field.label) {
      ToastsContainer.addWarning(
        'One of Placeholder or Label is required for this field.',
      );
      return this.setState({
        errors: {
          label: true,
          placeholder: true,
        },
      });
    }

    this.setState(
      {
        field: {
          ...this.state.field,
          placeholder,
        },
      },
      this.update,
    );
  };

  onSelectOptionChange = (select_options) => {
    const { field } = this.state;

    this.setState(
      {
        field: {
          ...field,
          select_options,
        },
      },
      this.update,
    );
  };

  onToggle = (attr) => {
    const { field } = this.state;

    this.setState(
      {
        field: {
          ...field,
          [attr]: !field[attr],
        },
      },
      this.update,
    );
  };

  onDefaultValueChange = (event) => {
    this.setState({
      field: {
        ...this.state.field,
        default_value: event.target.value,
      },
    });
  };

  onAutofillToggle = (event) => {
    const { checked } = event.target;
    const {
      field,
      field: { progressive },
    } = this.state;

    if (checked && !this.formCanBeAutofilled()) {
      return ToastsContainer.addWarning('Add an email field to use Autofill');
    }

    this.setState(
      {
        field: {
          ...field,
          autofill: checked,
          progressive: checked ? progressive : false,
        },
      },
      this.update,
    );
  };

  onProgressiveToggle = (event) => {
    const { checked } = event.target;
    const { field } = this.state;

    if (checked && !this.formCanBeAutofilled()) {
      return ToastsContainer.addWarning(
        'Add an email field to use Dynamic Enrichment',
      );
    }

    this.setState(
      {
        field: {
          ...field,
          progressive: checked,
          autofill: checked ? true : field.autofill,
        },
      },
      this.update,
    );
  };

  validate() {
    const { field } = this.state;
    const { fields } = this.props;
    let errors = {};
    let notification = false;

    if (!field.slug) {
      errors.slug = true;
      notification = 'Internal name is required.';
    }

    let otherFields = fields.filter((f) => f.id !== field.id);
    let otherFieldSlugs = otherFields.map((field) => field.slug);

    if (otherFieldSlugs.includes(field.slug)) {
      errors.slug = true;
      notification = 'Invalid internal_name value.';
    }

    this.setState({ errors });

    if (notification) ToastsContainer.addError(notification);

    return isEmpty(errors);
  }

  update = () => {
    const { field } = this.state;

    if (!this.validate()) return;
    if (isEqual(this.props.field, field)) return;

    this.props.onChange(field);
  };

  onDelete = () => {
    if (this.canRemoveField()) {
      return this.delete();
    }

    ToastsContainer.addWarning(
      'Disable autofill on other fields before removing email fields.',
    );

    this.setState({
      errors: {
        email: true,
      },
    });
  };

  delete = () => this.props.onChange(this.state.field, true);

  refreshForm = () => this.props.onRefreshForm();

  canRemoveField = () => {
    const { field } = this.state;
    if (field.type !== 'email') {
      return true;
    }

    return !this.formHasAutofillFields();
  };

  formCanBeAutofilled() {
    const { fields } = this.props;

    return fields.filter((f) => f.type === 'email').length > 0;
  }

  formHasAutofillFields() {
    const { fields } = this.props;

    return fields.filter((f) => f.autofill === true).length > 0;
  }

  renderTypeOptions() {
    return Object.entries(LABEL_FOR_FIELD_TYPE).map(([type, label]) => (
      <MenuItem value={type} key={type}>
        {label}
      </MenuItem>
    ));
  }

  render() {
    const { field, errors } = this.state;
    const { classes, onClose } = this.props;

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.closeWrapper} onClick={onClose}>
            <Close className={classes.close} />
          </div>
          <Typography type="h2" gutterBottom className={classes.title}>
            Edit field
          </Typography>
          <div className={classes.actions}>
            <IconButton onClick={this.onDelete}>
              <TrashcanIcon />
            </IconButton>
          </div>
        </div>
        <div className={classes.formRoot}>
          <div className={classes.formContent}>
            <div>
              <Grid container spacing={40}>
                <Grid item xs>
                  <div className={classes.input}>
                    <FormControl fullWidth>
                      <FormLabel>Field Type</FormLabel>
                      <Select
                        fullWidth
                        label="Field type"
                        value={field.type}
                        onChange={this.onTypeChange}>
                        {this.renderTypeOptions()}
                      </Select>
                    </FormControl>
                  </div>

                  {this.isVisible(field) && (
                    <TextFieldWithTooltip
                      fullWidth
                      labelValue="Field label"
                      name="label"
                      onBlur={this.update}
                      onChange={this.onLabelChange}
                      tooltipPlacement="right"
                      tooltipTitle="Field label to be displayed on form"
                      value={field.label}
                      wrapperClassName={classes.input}
                    />
                  )}

                  {field.type === 'checkbox' && (
                    <TextFieldWithTooltip
                      multiline
                      fullWidth
                      labelValue="Field description"
                      name="description"
                      onBlur={this.update}
                      onChange={this.onDescriptionChange}
                      tooltipPlacement="right"
                      tooltipTitle="Descriptive text shown on the same line as the checkbox - supports Markdown style links"
                      value={field.description || ''}
                      wrapperClassName={classes.input}
                    />
                  )}

                  {field.type === 'hidden' && (
                    <TextFieldWithTooltip
                      fullWidth
                      labelValue="Field Value"
                      name="default_value"
                      onChange={this.onDefaultValueChange}
                      onBlur={this.update}
                      tooltipPlacement="right"
                      tooltipTitle="Static value to submit with form"
                      value={field.default_value}
                      wrapperClassName={classes.input}
                    />
                  )}

                  {field.type === attributeTypes.SELECT && (
                    <div className={classes.input}>
                      <FormGroup>
                        <FormLabel>Options</FormLabel>
                        <SelectOptions
                          options={field.select_options}
                          onChange={this.onSelectOptionChange}
                        />
                      </FormGroup>
                    </div>
                  )}
                  <div className={classes.input}>
                    <TextFieldWithTooltip
                      fullWidth
                      required
                      error={errors.slug}
                      labelValue="Internal name"
                      value={field.slug || ''}
                      onChange={this.onSlugChange}
                      onBlur={this.update}
                      tooltipPlacement="right"
                      tooltipTitle="A unique field name used for various Clearbit functions"
                      wrapperClassName={classes.input}
                    />
                  </div>

                  {this.hasPlaceholder(field) && (
                    <TextFieldWithTooltip
                      fullWidth
                      labelValue="Field placeholder"
                      name="placeholder"
                      onBlur={this.update}
                      onChange={this.onPlaceholderChange}
                      tooltipPlacement="right"
                      tooltipTitle="Placeholder text displayed when an input is empty."
                      value={field.placeholder || ''}
                      wrapperClassName={classes.input}
                    />
                  )}
                </Grid>
              </Grid>
            </div>

            <SwitchGroup>
              {this.isVisible(field) && (
                <Hoverable
                  render={({ hovered, onMouseEnter, onMouseLeave }) => (
                    <SwitchGroupItem
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}>
                      <Tooltip
                        hovered={hovered}
                        title="Require user to fill out this field"
                        placement="right">
                        <div>
                          <Switch
                            label="Required"
                            checked={field.required || false}
                            onChange={() => this.onToggle('required')}
                          />
                        </div>
                      </Tooltip>
                    </SwitchGroupItem>
                  )}
                />
              )}
              {field.type === 'checkbox' && (
                <SwitchGroupItem>
                  <div>
                    <Switch
                      label="Checked (true) by default"
                      checked={field.default_value || false}
                      onChange={() => this.onToggle('default_value')}
                    />
                  </div>
                </SwitchGroupItem>
              )}

              {field.type === 'email' && (
                <Hoverable
                  render={({ hovered, onMouseEnter, onMouseLeave }) => (
                    <SwitchGroupItem
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}>
                      <Tooltip
                        hovered={hovered}
                        title="Block personal email providers like gmail.com"
                        placement="right">
                        <div>
                          <Switch
                            label="Block non-corporate emails"
                            checked={field.block_free_email_providers || false}
                            onChange={() =>
                              this.onToggle('block_free_email_providers')
                            }
                          />
                        </div>
                      </Tooltip>
                    </SwitchGroupItem>
                  )}
                />
              )}
              {field.type === 'country_code' && (
                <Hoverable
                  render={({ hovered, onMouseEnter, onMouseLeave }) => (
                    <SwitchGroupItem
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}>
                      <Tooltip
                        hovered={hovered}
                        title="Turn this option on to receive response data with full country names - i.e. 'United States' instead of 'US'."
                        placement="right">
                        <div>
                          <Switch
                            label="Send full country name"
                            checked={field.send_full_country === true}
                            onChange={() => this.onToggle('send_full_country')}
                          />
                        </div>
                      </Tooltip>
                    </SwitchGroupItem>
                  )}
                />
              )}
              {this.canAutofill(field) && (
                <Hoverable
                  render={({ hovered, onMouseEnter, onMouseLeave }) => (
                    <SwitchGroupItem
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}>
                      <Tooltip
                        hovered={hovered}
                        title={
                          this.formCanBeAutofilled()
                            ? 'Automatically fill field with Clearbit data.'
                            : 'Add an email field to use autofill.'
                        }
                        placement="right">
                        <div>
                          <Switch
                            label="Autofill"
                            checked={field.autofill || false}
                            onChange={this.onAutofillToggle}
                          />
                        </div>
                      </Tooltip>
                    </SwitchGroupItem>
                  )}
                />
              )}
              {this.canAutofill(field) && (
                <Hoverable
                  render={({ hovered, onMouseEnter, onMouseLeave }) => (
                    <SwitchGroupItem
                      onMouseEnter={onMouseEnter}
                      onMouseLeave={onMouseLeave}>
                      <Tooltip
                        hovered={hovered}
                        title={
                          this.formCanBeAutofilled()
                            ? 'Only show field if Clearbit doesn’t have data.'
                            : 'Add an email field to use Dynamic Enrichment.'
                        }
                        placement="right">
                        <div>
                          <Switch
                            label="Dynamic Enrichment"
                            checked={field.progressive || false}
                            onChange={this.onProgressiveToggle}
                          />
                        </div>
                      </Tooltip>
                    </SwitchGroupItem>
                  )}
                />
              )}
            </SwitchGroup>
          </div>
        </div>
      </div>
    );
  }
}

EditField.propTypes = {
  classes: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
  fields: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onRefreshForm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default withStyles(styles)(EditField);
